import { Events } from '../constants/webSocketConsts/events';
import { subscriptionsApi } from '../services/api/subscriptionsApi';
import { ensureUnreachable } from '../utils/common_utils';
import { Dispatch } from 'redux';
import { Subscription } from '../types/subscriptions';
import { arrayToMap } from 'src/utils/array';
import { Company } from 'src/store/clients/types';
import { Client } from 'src/constants';

const updateCache = (
  event: Events,
  items: Subscription[],
  draft?: Subscription[],
) => {
  console.info('updateCache', { draft, event, items });

  if (!draft) {
    return;
  }

  items.forEach((subObj) => {
    const upcomingSubscriptionIndex = draft.findIndex(
      (item: Subscription) => item.id === subObj.id,
    );

    switch (event) {
      case Events.Insert: {
        if (upcomingSubscriptionIndex !== -1) {
          return;
        }

        draft.unshift(subObj);
        break;
      }
      case Events.Modify:
        if (upcomingSubscriptionIndex === -1) {
          draft.unshift(subObj);
          return;
        }
        draft.splice(
          0,
          draft.length,
          ...draft.map((item: Subscription) =>
            item.id === subObj.id ? subObj : item,
          ),
        );
        break;
      case Events.Remove:
        if (upcomingSubscriptionIndex === -1) {
          return;
        }
        draft.splice(upcomingSubscriptionIndex, 1);
        break;
      case Events.Refresh:
      case Events.DomainVerified:
        return;
      default:
        ensureUnreachable(event);
    }
  });
};

export function handleSubscriptionUpdate(
  event: Events,
  items: Subscription[],
  dispatch: Dispatch<any>,
  companies: Company[],
  clients: Client[],
) {
  dispatch(
    subscriptionsApi.util.updateQueryData(
      'getSubscriptions',
      undefined,
      (draft?: Subscription[]) => {
        updateCache(event, items, draft);
      },
    ),
  );

  // Updating cache for each subscription for subscription edit view
  items.forEach((item) => {
    dispatch(
      subscriptionsApi.util.updateQueryData(
        'getSubscriptionById',
        item.id,
        (draft?: Subscription) => {
          if (!draft) {
            return;
          }
          Object.assign(draft, item);
        },
      ),
    );
  });

  for (const item of items) {
    const { companyId, clientUserId } = item.fields;

    if (companyId) {
      const companiesMap = arrayToMap(companies, 'id');
      // If found, this indicates that this is a company subscription
      const company = companiesMap.get(companyId);

      dispatch(
        subscriptionsApi.util.updateQueryData(
          'getSubscriptionsByRecipientId',
          companyId,
          (draft?: Subscription[]) => {
            updateCache(event, items, draft);
          },
        ),
      );
      if (company) {
        // If the company is found, we need to update the subscriptions of all clients under this company
        const companyClientsIds = clients
          .filter(
            (client) =>
              // TODO:(enable_many_companies) remove the companyId check once we enable many companies
              client.fields.companyId === companyId ||
              client.fields.companyIds?.includes(companyId),
          )
          .map((client) => client.id);
        for (const clientId of companyClientsIds) {
          dispatch(
            subscriptionsApi.util.updateQueryData(
              'getSubscriptionsByRecipientId',
              clientId,
              (draft?: Subscription[]) => {
                updateCache(event, items, draft);
              },
            ),
          );
        }
      }
    }

    if (clientUserId) {
      // If the client is found, we need to update the subscriptions of this client
      dispatch(
        subscriptionsApi.util.updateQueryData(
          'getSubscriptionsByRecipientId',
          clientUserId,
          (draft?: Subscription[]) => {
            updateCache(event, items, draft);
          },
        ),
      );
    }
  }
}
